import { cva, cx, VariantProps } from 'class-variance-authority';
import React, { forwardRef, useId } from 'react';
import { cn } from '~/lib/cn';

const labelClassNames = cva('tw-block tw-font-normal tw-text-sm tw-mb-1 tw-pl-1', {
    variants: {
        isInvalid: {
            true: 'tw-text-[#F31260]',
            false: 'tw-text-accents-9'
        }
    },
    defaultVariants: {
        isInvalid: false
    }
});

const helperTextClassNames = cva('tw-ml-2 tw-mt-1 tw-block tw-text-[10px]', {
    variants: {
        isInvalid: {
            true: 'tw-text-error',
            false: 'tw-text-accents-6'
        }
    },
    defaultVariants: {
        isInvalid: false
    }
});

const containerClassNames = cva('', {
    variants: {
        fullWidth: {
            true: 'tw-w-full',
            false: ''
        },
        flush: {
            true: 'tw-my-0',
            false: 'tw-my-1'
        }
    },
    defaultVariants: {
        fullWidth: false,
        flush: false
    }
});

const inputContainerClassNames = cva(
    'tw-px-2 tw-overflow-hidden tw-items-center tw-gap-2 tw-flex tw-w-full tw-border-solid tw-outline-none tw-transition-colors',
    {
        variants: {
            size: {
                xs: 'tw-text-xs tw-h-6',
                sm: 'tw-text-sm tw-h-8',
                md: 'tw-text-md tw-h-10',
                lg: 'tw-text-lg tw-h-12',
                xl: 'tw-text-xl tw-h-14'
            },
            isInvalid: {
                true: 'tw-bg-errorLight has-[:focus]:tw-border-error has-[:active]:tw-border-error',
                false: 'tw-bg-accents-0 has-[:focus]:tw-border-success has-[:active]:tw-border-success'
            },
            disabled: {
                true: 'tw-cursor-not-allowed tw-bg-accents-1 tw-text-accents-7',
                false: ''
            },
            bordered: {
                true: 'tw-border-2 tw-border-solid tw-border-accents-3',
                false: 'tw-border tw-border-transparent'
            },
            rounded: {
                true: 'tw-rounded-xl',
                false: ''
            }
        },
        defaultVariants: {
            isInvalid: false,
            disabled: false,
            bordered: false,
            size: 'sm',
            rounded: true
        },
        compoundVariants: [
            {
                bordered: true,
                disabled: false,
                className: 'tw-bg-white'
            }
        ]
    }
);

const inputClassNames = cva('tw-text-sm tw-h-full tw-flex-1 tw-border-none tw-bg-transparent tw-py-2 tw-outline-none', {
    variants: {
        disabled: {
            true: 'tw-cursor-not-allowed',
            false: ''
        }
    },
    defaultVariants: {
        disabled: false
    }
});

const sideLabel = cva(
    'tw-flex tw-shrink-0 *:tw-overflow-hidden  tw-h-full tw-px-2 *:tw-text-ellipsis tw-flex-col tw-justify-center *:tw-whitespace-nowrap tw-max-w-[120px] tw-overflow-hidden',
    {
        variants: {
            bordered: {
                true: 'tw-bg-transparent tw-text-accents-6 tw-text-sm',
                false: 'tw-bg-accents-2 tw-text-accents-8 tw-text-sm'
            }
        },
        defaultVariants: {
            bordered: false
        }
    }
);

type CombinedVariantProps = VariantProps<typeof inputClassNames> &
    VariantProps<typeof inputContainerClassNames> &
    VariantProps<typeof containerClassNames> &
    VariantProps<typeof labelClassNames>;

export interface StyledInputProps
    extends Omit<React.InputHTMLAttributes<HTMLInputElement>, keyof CombinedVariantProps>, CombinedVariantProps {
    name?: string;
    label?: any;
    isInvalid?: boolean;
    helperText?: React.ReactNode;
    containerClassName?: string;
    inputClassName?: string;
    /**
     * Overrides the helper line's own type and spacing.
     *
     * For a panel that sets hints itself and puts inputs among them: the helper
     * is 10px and indented, a standalone hint beside it is not, and stacked in
     * one column the two read as two different kinds of remark. Opt-in, so every
     * existing form keeps the helper it has.
     */
    helperTextClassName?: string;
    labelLeft?: string;
    labelRight?: string;
    contentRight?: React.ReactNode;
    contentLeft?: React.ReactNode;
    /** @deprecated not needed */
    animated?: boolean;
    /** @deprecated removed */
    clearable?: boolean;
    /** @deprecated removed */
    css?: any;
    /** @deprecated removed */
    underlined?: any;
    /** @deprecated removed */
    initialValue?: any;
    contentRightStyling?: boolean;
    contentLeftStyling?: boolean;
}

const StyledInput = forwardRef<HTMLInputElement, StyledInputProps>(
    (
        {
            containerClassName,
            inputClassName,
            className,
            name,
            flush,
            isInvalid,
            css,
            initialValue,
            underlined,
            contentRightStyling = true,
            contentLeftStyling = true,
            fullWidth,
            helperText,
            helperTextClassName,
            label,
            contentLeft,
            contentRight,
            labelLeft,
            labelRight,
            size,
            animated,
            bordered,
            id,
            ...props
        }: StyledInputProps,
        ref
    ) => {
        // Many callers pass neither id nor name, which used to give every such field
        // id="input_undefined" — duplicate ids, and labels pointing at the wrong input.
        const generatedId = useId();
        const inputId = id ?? (name ? 'input_' + name : generatedId);
        const helperId = helperText ? inputId + '_helper' : undefined;
        const describedBy = [props['aria-describedby'], helperId].filter(Boolean).join(' ') || undefined;
        return (
            <div className={containerClassNames({ className, flush, fullWidth })}>
                {label && (
                    <label htmlFor={inputId} className={labelClassNames({ isInvalid })}>
                        {label}
                    </label>
                )}
                <div
                    className={inputContainerClassNames({
                        className: containerClassName,
                        size,
                        isInvalid,
                        bordered,
                        ...props
                    })}
                >
                    {labelLeft && (
                        <div className={sideLabel({ className: '-tw-ml-2', ...props })}>
                            <span>{labelLeft}</span>
                        </div>
                    )}
                    {contentLeft && <div className={cx(!contentRightStyling && '-tw-ml-2')}>{contentLeft}</div>}
                    <input
                        ref={ref}
                        id={inputId}
                        name={name}
                        className={inputClassNames({ className: inputClassName, ...props })}
                        aria-invalid={isInvalid || undefined}
                        {...props}
                        aria-describedby={describedBy}
                    />
                    {contentRight && <div className={cx(!contentRightStyling && '-tw-mr-2')}>{contentRight}</div>}
                    {labelRight && (
                        <div className={sideLabel({ className: '-tw-mr-2', ...props })}>
                            <span>{labelRight}</span>
                        </div>
                    )}
                </div>
                {/* `cn`, not cva's own `className`: cva only appends, and which of two
                    Tailwind sizes wins is then decided by their order in the stylesheet
                    rather than by the caller. */}
                {helperText && (
                    <span id={helperId} className={cn(helperTextClassNames({ isInvalid }), helperTextClassName)}>
                        {helperText}
                    </span>
                )}
            </div>
        );
    }
);

StyledInput.displayName = 'StyledInput';

export { containerClassNames, helperTextClassNames, inputClassNames, inputContainerClassNames, labelClassNames };

export default StyledInput;
